import { useQuery } from '@tanstack/react-query'

import {
  expectGpuOk,
  gpuAdminConfigured,
  gpuClient,
  type GpuSchemas,
  unwrapGpu,
} from '@/api/gpu/client'

import { GPU_KEY } from './useGpuAdmin'
import { useGpuMutation } from './useGpuMutations'

// Sales rules an operator sets on top of the catalog
// (graph @cloudless/fluence, #3113, #3115, #3117).
export type AkashBlock = GpuSchemas['BlockView']
export type CountryExclusion = GpuSchemas['CountryExclusionView']
export type OfferExclusion = GpuSchemas['OfferExclusionView']

export function useAkashBlocks() {
  return useQuery({
    queryKey: [...GPU_KEY, 'akash-blocks'],
    enabled: gpuAdminConfigured,
    queryFn: async () =>
      unwrapGpu(
        await gpuClient.GET('/gpu/admin/v1/akash/provider-blocks'),
        'Failed to load Akash provider blocks',
      ),
  })
}

export function useCountryExclusions(provider: string) {
  return useQuery({
    queryKey: [...GPU_KEY, 'excluded-countries', provider],
    enabled: gpuAdminConfigured,
    queryFn: async () =>
      unwrapGpu(
        await gpuClient.GET(
          '/gpu/admin/v1/providers/{provider}/excluded-countries',
          { params: { path: { provider } } },
        ),
        'Failed to load excluded countries',
      ),
  })
}

export function useOfferExclusions(provider: string) {
  return useQuery({
    queryKey: [...GPU_KEY, 'excluded-offers', provider],
    enabled: gpuAdminConfigured,
    queryFn: async () =>
      unwrapGpu(
        await gpuClient.GET(
          '/gpu/admin/v1/providers/{provider}/excluded-offers',
          { params: { path: { provider } } },
        ),
        'Failed to load excluded offers',
      ),
  })
}

interface Change {
  key: string
  reason?: string
}

export function useAkashBlockChange() {
  const block = useGpuMutation(
    async ({ key, reason = '' }: Change) =>
      unwrapGpu(
        await gpuClient.PUT('/gpu/admin/v1/akash/provider-blocks/{owner}', {
          params: { path: { owner: key } },
          body: { reason },
        }),
        'Failed to block provider',
      ),
    'Provider blocked',
  )
  const unblock = useGpuMutation(
    async ({ key }: Change) =>
      expectGpuOk(
        await gpuClient.DELETE('/gpu/admin/v1/akash/provider-blocks/{owner}', {
          params: { path: { owner: key } },
        }),
        'Failed to unblock provider',
      ),
    'Provider unblocked',
  )
  return { block, unblock }
}

export function useCountryExclusionChange(provider: string) {
  const path = (country: string) => ({ path: { provider, country } })
  const exclude = useGpuMutation(
    async ({ key, reason = '' }: Change) =>
      expectGpuOk(
        await gpuClient.PUT(
          '/gpu/admin/v1/providers/{provider}/excluded-countries/{country}',
          { params: path(key), body: { reason } },
        ),
        'Failed to exclude country',
      ),
    'Country excluded',
  )
  const allow = useGpuMutation(
    async ({ key }: Change) =>
      expectGpuOk(
        await gpuClient.DELETE(
          '/gpu/admin/v1/providers/{provider}/excluded-countries/{country}',
          { params: path(key) },
        ),
        'Failed to return country to sale',
      ),
    'Country returned to sale',
  )
  return { exclude, allow }
}

export function useOfferExclusionChange(provider: string) {
  const path = (id: string) => ({
    path: { provider, provider_offer_id: id },
  })
  const exclude = useGpuMutation(
    async ({ key, reason = '' }: Change) =>
      expectGpuOk(
        await gpuClient.PUT(
          '/gpu/admin/v1/providers/{provider}/excluded-offers/{provider_offer_id}',
          { params: path(key), body: { reason } },
        ),
        'Failed to take offer off sale',
      ),
    'Offer taken off sale',
  )
  const allow = useGpuMutation(
    async ({ key }: Change) =>
      expectGpuOk(
        await gpuClient.DELETE(
          '/gpu/admin/v1/providers/{provider}/excluded-offers/{provider_offer_id}',
          { params: path(key) },
        ),
        'Failed to return offer to sale',
      ),
    'Offer returned to sale',
  )
  return { exclude, allow }
}
